import axios, { AxiosResponse } from 'axios'
import { useState, useEffect } from 'react'
import { TFunction } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { encryptAES } from '../../util/cipher'
import { validateISYL } from '../../util/validate'
import useLoadingStore from '../../stores/useLoadingStore'
import ConfigAPI from '../../constant/ConfigAPI'

export type Response = { result?: AxiosResponse<any>; error?: any }

export interface Form {
    newPassword?: string
    confirmNewPassword?: string
}

export const validate = async (values: Form, t: TFunction<'translation'>) => {
    const errors: Partial<Form> = {}
    if (!values.newPassword) {
        errors.newPassword = `${t('LABEL.TEXT_REQUIRED')}`
    }
    if (values.newPassword && !validateISYL(encryptAES(values.newPassword))) {
        errors.newPassword = `${t('VALIDATE.MESSAGES.PASSWORD')}`
    }
    if (!values.confirmNewPassword) {
        errors.confirmNewPassword = `${t('LABEL.TEXT_REQUIRED')}`
    }
    if (values.confirmNewPassword && values.confirmNewPassword !== values.newPassword) {
        errors.confirmNewPassword = `${t('VALIDATE.MESSAGES.CONFIRM_PASSWORD')}`
    }
    return errors
}

const useResetISYL = () => {
    const [userId, setUserId] = useState<number>(0)
    const [displaySubmitButton, setDisplaySubmitButton] = useState<boolean>(false)
    const [openDialogOk, setOpenDialogOk] = useState<boolean>(false)
    const [openDialogError, setOpenDialogError] = useState<boolean>(false)
    const [statusCode, setStatusCode] = useState<number>()
    const loadingStore = useLoadingStore()
    const history = useHistory()
    const [alertMessageOk, setAlertMessageOk] = useState<string>('')
    const [alertMessageError, setAlertMessageError] = useState<string>('')

    useEffect(() => {
        const splitUrl = globalThis.location.href.split('/')
        const refId = splitUrl.pop()
        if (refId) {
            callServiceVerifyResetPwd(refId)
        }
    }, [])

    const callServiceVerifyResetPwd = async (refId: string) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/auth/verifyResetPassword/${refId}`
            const result = await axios.get(url, ConfigAPI)
            if (result.data.message === 'success') {
                setDisplaySubmitButton(true)
                setUserId(result.data.userId)
            }
            return { result }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                setDisplaySubmitButton(false)
                if (errMsg.includes('401')) {
                    setStatusCode(401.1)
                    setOpenDialogOk(true)
                    setAlertMessageOk('ลิงก์ของท่านหมดอายุ ไม่สามารถกำหนดรหัสผ่านใหม่ได้')
                } else {
                    setStatusCode(500.1)
                    setOpenDialogError(true)
                    setAlertMessageError('ไม่สามารถกำหนดรหัสผ่านใหม่ได้')
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceResetISYL = async (userId: number, encryptedPwd: string) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/auth/resetPassword/${userId}`
            const reqParams = {
                newPassword: encryptedPwd,
            }
            const result = await axios.post(url, reqParams, ConfigAPI)
            if (result.status === 200) {
                setStatusCode(200)
                setAlertMessageOk('บันทึกรหัสผ่านใหม่สำเร็จ')
                setOpenDialogOk(true)
            }
            return { result }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                if (errMsg.includes('401')) {
                    setStatusCode(401.2)
                    setOpenDialogOk(true)
                    setAlertMessageOk('ลิงก์ของท่านหมดอายุ ไม่สามารถกำหนดรหัสผ่านใหม่ได้')
                } else {
                    setStatusCode(500.2)
                    setOpenDialogError(true)
                    setAlertMessageError('ไม่สามารถบันทึกรหัสผ่านใหม่ได้')
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const handleSubmitForm = (values: any) => {
        callServiceResetISYL(userId, encryptAES(values.newPassword as string))
    }

    const handleClickOk = () => {
        setOpenDialogOk(false)
        if (statusCode === 200) history.push('/login')
        else history.push('/')
    }

    return {
        onSubmitForm: handleSubmitForm,
        onClickOk: handleClickOk,
        displaySubmitButton,
        openDialogOk,
        alertMessageOk,
        openDialogError,
        alertMessageError,
    }
}

export default useResetISYL
